import React, { useEffect, useRef, useState } from 'react';
import { loadRuntime, hasWebGL2 } from '../lib/runtime.js';

const reduced = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A section demo: a Pulse player created only when it first scrolls near the viewport, paused
 * whenever it is off screen, with a play/pause control. `build(Pulse)` returns the document.
 */
export default function MiniPlayer({ build, label, aspect = '16 / 9', onPlayer, options, className = '' }) {
  const box = useRef(null);
  const stage = useRef(null);
  const player = useRef(null);
  const want = useRef(!reduced());
  const [state, setState] = useState('idle');
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!hasWebGL2()) { setState('nogl'); return undefined; }
    let cancelled = false, visible = false;
    const sync = () => {
      const p = player.current; if (!p) return;
      if ((!visible || document.hidden) && !p.paused) p.pause();
      else if (visible && !document.hidden && p.paused && want.current) p.play().catch(() => {});
    };
    const io = new IntersectionObserver(async (entries) => {
      visible = entries[0].isIntersecting;
      if (visible && !player.current && state !== 'loading') {
        setState('loading');
        try {
          const Pulse = await loadRuntime();
          if (cancelled) return;
          const doc = build(Pulse);
          const p = await Pulse.load(doc, { container: stage.current, loop: true, autoplay: want.current, audio: false, previewSignals: true, ...(options || {}) });
          if (cancelled) { p.destroy(); return; }
          player.current = p;
          p.on('play', () => setPlaying(true));
          p.on('pause', () => setPlaying(false));
          setPlaying(!p.paused);
          setState('ready');
          if (onPlayer) onPlayer(p, Pulse, doc);
        } catch (e) {
          if (!cancelled) setState('error');
        }
      }
      sync();
    }, { rootMargin: '200px 0px', threshold: 0 });
    io.observe(box.current);
    document.addEventListener('visibilitychange', sync);
    return () => {
      cancelled = true;
      io.disconnect();
      document.removeEventListener('visibilitychange', sync);
      if (player.current) { player.current.destroy(); player.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    const p = player.current; if (!p) return;
    if (p.paused) { want.current = true; p.play().catch(() => {}); } else { want.current = false; p.pause(); }
  };

  return (
    <div className={`mini wx-stage ${className}`} ref={box} style={{ aspectRatio: aspect }}>
      <div className="mini-stage" ref={stage} role="img" aria-label={label} />
      {state !== 'ready' && (
        <div className="mini-msg">
          {state === 'nogl' ? 'This demo needs WebGL2.' : state === 'error' ? 'The demo could not start.' : 'Loading…'}
        </div>
      )}
      {state === 'ready' && (
        <button type="button" className="mini-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? <PauseIcon /> : <PlayIcon />}
        </button>
      )}
    </div>
  );
}

const PlayIcon = () => <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M4 2.5v11l9-5.5z" fill="currentColor" /></svg>;
const PauseIcon = () => <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" fill="currentColor" /></svg>;
